<template>
  <section class="tool-section" id="tool">
    <div class="container">
      <div class="tool-header">
        <span class="step-tag">{{ t('tool.tag') }}</span>
        <h2 class="serif">{{ t('tool.title') }}</h2>
        <p>{{ t('tool.subtitle') }}</p>
      </div>

      <ToolForm @result="onResult" />

      <ProcessingSteps
        v-if="processing"
        :woz="wozData"
        :currentStep="currentStep"
        :completedSteps="completedSteps"
      />

      <div v-if="errorMessage" class="error-card show">
        <h3>{{ t('tool.errorTitle') }}</h3>
        <p>{{ errorMessage }}</p>
      </div>

      <ResultSection
        v-if="resultData"
        :data="resultData"
        @openBrief="onOpenBrief"
      />
    </div>
  </section>
</template>

<script setup>
import { ref } from 'vue'
import ToolForm from './ToolForm.vue'
import ProcessingSteps from './ProcessingSteps.vue'
import ResultSection from './ResultSection.vue'
import { useLocale } from '../composables/useLocale'

const { t } = useLocale()

const emit = defineEmits(['openBrief'])

const processing = ref(false)
const currentStep = ref(0)
const completedSteps = ref([])
const wozData = ref(null)
const resultData = ref(null)
const errorMessage = ref(null)

function onOpenBrief(briefNummer) {
  if (resultData.value) {
    emit('openBrief', { briefNummer, data: resultData.value })
  }
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

async function onResult(data) {
  errorMessage.value = null
  resultData.value = null
  processing.value = true
  currentStep.value = 1
  completedSteps.value = []
  wozData.value = null

  await sleep(100)
  const processingEl = document.querySelector('.processing')
  if (processingEl) processingEl.scrollIntoView({ behavior: 'smooth', block: 'center' })

  await sleep(900)
  wozData.value = data.woz
  completedSteps.value = [1]
  currentStep.value = 2

  await sleep(500)
  completedSteps.value = [1, 2]
  currentStep.value = 3

  await sleep(500)
  completedSteps.value = [1, 2, 3]
  currentStep.value = 4

  await sleep(500)
  completedSteps.value = [1, 2, 3, 4]
  currentStep.value = 5

  await sleep(500)
  completedSteps.value = [1, 2, 3, 4, 5]

  processing.value = false
  resultData.value = data

  await sleep(200)
  const resultEl = document.querySelector('.result')
  if (resultEl) resultEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
</script>

<style scoped>
.tool-section { padding: 40px 0 80px; }
@media (max-width: 768px) { .tool-section { padding: 40px 0 48px; } .tool-header { margin-bottom: 36px; } }
.tool-header { text-align: center; margin-bottom: 56px; }
.tool-header .step-tag {
  display: inline-block;
  background: var(--accent-light);
  color: var(--ink);
  border-radius: 999px;
  padding: 8px 18px;
  font-weight: 700;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin-bottom: 24px;
}
.tool-header h2 {
  font-size: clamp(40px, 6vw, 72px);
  font-weight: 800;
  line-height: 0.98;
  letter-spacing: -0.03em;
  margin-bottom: 20px;
}
.tool-header p {
  font-size: 18px;
  color: var(--ink-soft);
  max-width: 72ch;
  margin: 0 auto;
}

.error-card {
  max-width: 720px;
  margin: 32px auto 0;
  background: var(--blue);
  color: var(--bg);
  border: none;
  padding: 28px 36px;
  border-radius: 24px;
  box-shadow: var(--shadow-soft);
  animation: revealUp 0.6s ease;
}
.error-card h3 {
  font-family: 'Fraunces', serif;
  font-size: 24px;
  font-weight: 800;
  margin-bottom: 8px;
  letter-spacing: -0.02em;
}
.error-card p { font-size: 16px; line-height: 1.55; }
</style>
